/**
 * Narrow catalog rule bundles into a confident core path + conditionals.
 * Catalog rules often list every typical city review; citizens need "start here"
 * first, then maybe-apps gated by scoping answers — not a flat dump of everything.
 *
 * City packages supply `catalog.recommendationPolicy` (+ optional per-rule
 * `applicationRoles`) so Berkeley / Oakland / etc. get the same focus treatment
 * without hardcoding SF application-type ids in code.
 */

function truthy(v) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v == null || v === '') return false;
  if (typeof v === 'string') return ['1', 'true', 'yes', 'y', 'on'].includes(v.trim().toLowerCase());
  return Boolean(v);
}

function answeredBool(v) {
  return typeof v === 'boolean' || v === 0 || v === 1
    || ['true', 'false', 'yes', 'no', '0', '1'].includes(String(v ?? '').trim().toLowerCase());
}

function toRegex(value) {
  if (!value) return null;
  if (value instanceof RegExp) return value;
  if (typeof value === 'string' && value.trim()) {
    try {
      return new RegExp(value, 'i');
    } catch {
      return null;
    }
  }
  return null;
}

function normalizeGate(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    ...raw,
    keywords: toRegex(raw.keywords) || null,
  };
}

/** Built-in defaults (SF-shaped ids). Catalogs merge/override via recommendationPolicy. */
const DEFAULT_GATES = {
  health_permit: { scope: 'foodHandling' },
  activity_permit: { anyScope: ['servesAlcohol', 'outdoorSeating', 'foodHandling'] },
  place_of_assembly: {
    keywords: /\b(assembly|over\s*49|more than 49|nightclub|theater seating|capacity)\b/i,
  },
  change_of_use: {
    keywords: /\b(change of use|change occupancy|convert to|residential to commercial|office to retail)\b/i,
    defaultMaybe: true,
  },
  fire_life_safety: {
    anyScopePromote: ['foodHandling', 'structuralChange'],
    defaultMaybe: true,
  },
};

const DEFAULT_CORE_IDS = [
  'business_registration',
  'zoning_compliance',
  'building_permit',
  'adu_planning',
  'solar_permit',
  'electrical_permit',
  'plumbing_permit',
  'mechanical_permit',
  // Berkeley / common aliases
  'business_license',
  'zoning_certificate',
  'adu_building',
];

function mergePolicy(policy = {}) {
  const coreIds = new Set([
    ...DEFAULT_CORE_IDS,
    ...(Array.isArray(policy.coreIds) ? policy.coreIds : []),
  ]);
  const gates = { ...DEFAULT_GATES };
  Object.entries(policy.gates || {}).forEach(([id, gate]) => {
    const normalized = normalizeGate(gate);
    if (normalized) gates[id] = { ...(gates[id] || {}), ...normalized };
  });
  const shortBundleMax = Number.isFinite(policy.shortBundleMax)
    ? policy.shortBundleMax
    : 3;
  const alwaysKeepRuleIds = new Set([
    'adu',
    'solar',
    ...(Array.isArray(policy.alwaysKeepRuleIds) ? policy.alwaysKeepRuleIds : []),
  ]);
  return { coreIds, gates, shortBundleMax, alwaysKeepRuleIds };
}

/**
 * Resolve explicit role from rule.applicationRoles[id].
 * Values: "start" | "required" | "maybe" | { role, ...gate }
 */
function resolveRuleRole(ruleRoles, typeId) {
  if (!ruleRoles || typeof ruleRoles !== 'object') return null;
  const entry = ruleRoles[typeId];
  if (!entry) return null;
  if (typeof entry === 'string') return { role: entry, gate: null };
  if (typeof entry === 'object') {
    return {
      role: entry.role || 'required',
      gate: normalizeGate(entry),
    };
  }
  return null;
}

/**
 * @param {object} opts
 * @param {object[]} opts.applicationTypes
 * @param {string} opts.primaryTrack
 * @param {string|null} opts.matchedRuleId
 * @param {object} [opts.matchedRule] — catalog rule (may include applicationRoles)
 * @param {object} [opts.policy] — catalog.recommendationPolicy
 * @param {object} opts.intent
 * @param {object} opts.scopingAnswers
 * @param {number} opts.baseConfidence
 * @param {boolean} opts.needsFollowUp
 */
export function refineApplicationRecommendations({
  applicationTypes = [],
  primaryTrack = 'permit',
  matchedRuleId = null,
  matchedRule = null,
  policy = null,
  intent = {},
  scopingAnswers = {},
  baseConfidence = 0.5,
  needsFollowUp = false,
} = {}) {
  const {
    coreIds, gates, shortBundleMax, alwaysKeepRuleIds,
  } = mergePolicy(policy || {});
  const ruleRoles = matchedRule?.applicationRoles
    || (matchedRule && typeof matchedRule === 'object' ? matchedRule.applicationRoles : null)
    || null;

  const scoped = {
    ...(scopingAnswers && typeof scopingAnswers === 'object' ? scopingAnswers : {}),
  };
  ['servesAlcohol', 'outdoorSeating', 'foodHandling', 'structuralChange'].forEach((k) => {
    if (scoped[k] === undefined && intent[k] !== undefined) scoped[k] = intent[k];
  });
  const hay = [
    intent.description,
    intent.proposedUse,
    intent.useQuery,
    intent.projectType,
    intent.propertyType,
    ...(Array.isArray(intent.tags) ? intent.tags : []),
  ].filter(Boolean).join(' ');

  const kept = [];
  const maybe = [];
  const dropped = [];

  const applyGate = (gate, currentRole) => {
    let role = currentRole;
    let decision = 'keep';
    if (!gate) return { role, decision };

    if (gate.scope) {
      const v = scoped[gate.scope];
      if (answeredBool(v) && !truthy(v)) decision = 'drop';
      else if (answeredBool(v) && truthy(v)) role = role === 'start' ? 'start' : 'required';
      else decision = 'maybe';
    } else if (gate.anyScope) {
      const answered = gate.anyScope.filter((k) => answeredBool(scoped[k]));
      const anyTrue = gate.anyScope.some((k) => answeredBool(scoped[k]) && truthy(scoped[k]));
      const allFalse = answered.length === gate.anyScope.length
        && gate.anyScope.every((k) => !truthy(scoped[k]));
      if (anyTrue) role = 'required';
      else if (allFalse) decision = 'drop';
      else decision = 'maybe';
    } else if (gate.keywords) {
      if (gate.keywords.test(hay)) role = 'required';
      else if (gate.defaultMaybe) decision = 'maybe';
    } else if (gate.defaultMaybe) {
      if (gate.anyScopePromote?.some((k) => answeredBool(scoped[k]) && truthy(scoped[k]))) {
        role = 'required';
      } else if (
        gate.anyScopePromote
        && gate.anyScopePromote.every((k) => answeredBool(scoped[k]) && !truthy(scoped[k]))
      ) {
        decision = 'drop';
      } else {
        decision = 'maybe';
      }
    } else if (gate.role === 'maybe') {
      decision = 'maybe';
    }
    return { role, decision };
  };

  (applicationTypes || []).forEach((t, index) => {
    if (!t?.id) return;
    const explicit = resolveRuleRole(ruleRoles, t.id);
    let role = explicit?.role || 'required';
    let decision = 'keep';

    if (!explicit) {
      if (t.track === primaryTrack && !kept.some((x) => x.role === 'start')) {
        role = 'start';
      } else if (coreIds.has(t.id) || t.track === primaryTrack) {
        role = index === 0 ? 'start' : 'required';
      }
    }

    const gate = explicit?.gate || gates[t.id] || null;
    if (explicit?.role === 'maybe' && !gate) {
      decision = 'maybe';
    } else if (gate) {
      ({ role, decision } = applyGate(gate, role));
    }

    // Short / ADU-style bundles: keep all as core when no gate forced maybe/drop.
    if (
      decision === 'keep'
      && !gate
      && !explicit
      && (applicationTypes.length <= shortBundleMax || alwaysKeepRuleIds.has(matchedRuleId))
    ) {
      if (t.track === primaryTrack && !kept.some((x) => x.role === 'start') && maybe.every((x) => x.role !== 'start')) {
        role = 'start';
      }
    }

    const row = {
      ...t,
      role: decision === 'maybe' ? 'maybe' : role,
      certainty: decision === 'maybe' ? 'conditional' : (role === 'start' ? 'high' : 'required'),
    };
    if (decision === 'drop') {
      dropped.push(t.id);
    } else if (decision === 'maybe') {
      maybe.push(row);
    } else {
      kept.push(row);
    }
  });

  if (kept.length && !kept.some((t) => t.role === 'start')) {
    kept[0] = { ...kept[0], role: 'start', certainty: 'high' };
  }

  let confidence = typeof baseConfidence === 'number' ? baseConfidence : 0.5;
  if (!matchedRuleId) confidence = Math.min(confidence, 0.45);
  if (needsFollowUp) confidence = Math.min(confidence, 0.78);
  if (!needsFollowUp && maybe.length === 0 && kept.length > 0 && kept.length <= 3) {
    confidence = Math.min(0.94, Math.max(confidence, 0.86));
  } else if (!needsFollowUp && maybe.length === 0 && dropped.length > 0) {
    confidence = Math.min(0.92, confidence + 0.04);
  } else if (maybe.length > 0) {
    confidence = Math.min(confidence, 0.82);
  }

  return {
    applicationTypes: kept,
    maybeApplicationTypes: maybe,
    droppedApplicationTypeIds: dropped,
    confidence,
    recommendationMode: maybe.length ? 'focused_with_conditionals' : 'focused',
  };
}

export default refineApplicationRecommendations;
